import { Box, Grow, Stack, Typography } from '@mui/material'
import React from 'react'
import { useTranslation } from 'react-i18next'
import {
  useWatch,
  Control,
  UseFormTrigger,
  UseFormGetValues,
  UseFormResetField,
  UseFormSetValue,
} from 'react-hook-form'

import SquareMeters from '../common/SquareMeters'
// estate
import EstateFields from './EstateFields'
// land
import LandPlotFields from './LandPlotFields'
import LandPlotOfLandFields from './LandPlotOfLandFields'
import LandIslandFields from './LandIslandFields'
// commercial
import CommercialBuildingFields from './CommercialBuildingFields'
import CommercialOfficeFields from './CommercialOfficeFields'
import CommercialStoreFields from './CommercialStoreFields'
import CommercialIndustrialSpace from './CommercialIndustrialSpace'
import CommercialParkingFields from './CommercialParkingFields'
import CommercialHotelFields from './CommercialHotelFields'
import { FormValues } from '..'

interface Props {
  control: Control<FormValues>
  trigger: UseFormTrigger<FormValues>
  getValues: UseFormGetValues<FormValues>
  resetField: UseFormResetField<FormValues>
  setValue: UseFormSetValue<FormValues>
}

const Step3 = (props: Props) => {
  const { control, trigger, getValues, resetField, setValue } = props
  const { t } = useTranslation()

  const watchCategory = useWatch({
    control,
    name: 'step1.category',
  })
  const watchSubCategory = useWatch({
    control,
    name: 'step1.subCategory',
  })

  const propertyForms = {
    estate: {
      appartment: (
        <EstateFields
          control={control}
          getValues={getValues}
          resetField={resetField}
          setValue={setValue}
          trigger={trigger}
        />
      ),
      single_residence: (
        <EstateFields
          control={control}
          getValues={getValues}
          resetField={resetField}
          setValue={setValue}
          trigger={trigger}
        />
      ),
      maisonette: (
        <EstateFields
          control={control}
          getValues={getValues}
          resetField={resetField}
          setValue={setValue}
          trigger={trigger}
        />
      ),
      building: (
        <EstateFields
          control={control}
          getValues={getValues}
          resetField={resetField}
          setValue={setValue}
          trigger={trigger}
        />
      ),
      rest_types: (
        <EstateFields
          control={control}
          getValues={getValues}
          resetField={resetField}
          setValue={setValue}
          trigger={trigger}
        />
      ),
    },
    commercial: {
      office: (
        <CommercialOfficeFields
          control={control}
          getValues={getValues}
          resetField={resetField}
          setValue={setValue}
          trigger={trigger}
        />
      ),
      hall: (
        <CommercialOfficeFields
          control={control}
          getValues={getValues}
          resetField={resetField}
          setValue={setValue}
          trigger={trigger}
        />
      ),
      rest_commercial_types: (
        <CommercialOfficeFields
          control={control}
          getValues={getValues}
          resetField={resetField}
          setValue={setValue}
          trigger={trigger}
        />
      ),
      store: (
        <CommercialStoreFields
          control={control}
          getValues={getValues}
          resetField={resetField}
          setValue={setValue}
          trigger={trigger}
        />
      ),
      industrial_area: (
        <CommercialIndustrialSpace
          control={control}
          getValues={getValues}
          resetField={resetField}
          setValue={setValue}
          trigger={trigger}
        />
      ),
      craft_space: (
        <CommercialIndustrialSpace
          control={control}
          getValues={getValues}
          resetField={resetField}
          setValue={setValue}
          trigger={trigger}
        />
      ),
      warehouse: (
        <CommercialIndustrialSpace
          control={control}
          getValues={getValues}
          resetField={resetField}
          setValue={setValue}
          trigger={trigger}
        />
      ),
      hotel: (
        <CommercialHotelFields
          control={control}
          getValues={getValues}
          resetField={resetField}
          setValue={setValue}
          trigger={trigger}
        />
      ),
      commercial_building: (
        <CommercialBuildingFields
          control={control}
          getValues={getValues}
          resetField={resetField}
          setValue={setValue}
          trigger={trigger}
        />
      ),
      parking: (
        <CommercialParkingFields
          control={control}
          getValues={getValues}
          resetField={resetField}
          setValue={setValue}
          trigger={trigger}
        />
      ),
    },
    land: {
      plot_of_land: (
        <LandPlotOfLandFields
          control={control}
          getValues={getValues}
          resetField={resetField}
          setValue={setValue}
          trigger={trigger}
        />
      ),
      island: (
        <LandIslandFields
          control={control}
          getValues={getValues}
          resetField={resetField}
          setValue={setValue}
          trigger={trigger}
        />
      ),
      plot: (
        <LandPlotFields
          control={control}
          getValues={getValues}
          resetField={resetField}
          setValue={setValue}
          trigger={trigger}
        />
      ),
      other_land_types: (
        <LandPlotFields
          control={control}
          getValues={getValues}
          resetField={resetField}
          setValue={setValue}
          trigger={trigger}
        />
      ),
    },
  }

  const matchedComponent =
    propertyForms?.[watchCategory]?.[watchSubCategory] ?? null

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Stack spacing={3}>
          {!watchCategory || !watchSubCategory ? null : (
            <Typography
              component="span"
              display="inline-block"
              color="error"
              sx={{ marginBottom: 4 }}
            >
              {`(*) ${t(
                'manageEstateAd.steps.estateDescription.requiredFields'
              )}`}
            </Typography>
          )}

          {!watchCategory || !watchSubCategory ? (
            <Typography
              component="div"
              variant="h6"
              align="center"
              color="error"
              sx={{ py: 12 }}
            >
              {`${t('manageEstateAd.catOrSubNotSelected')}`}
            </Typography>
          ) : null}

          <Grow in={!!watchCategory && !!watchSubCategory} unmountOnExit>
            <Stack spacing={3}>
              <SquareMeters control={control} />
              {matchedComponent}
            </Stack>
          </Grow>
        </Stack>
      </Box>
    </>
  )
}

export default React.memo(Step3)
